import React from "react";
import PropTypes from "prop-types";
import {
  Container,
  Header,
  HeaderLeft,
  HeaderRight,
  Title,
  Year,
  EvaluationsBadge,
  MainContent,
  ScoreCardsContainer,
  ScoreCard,
  ScoreCardTitle,
  CircularChart,
  ChartsSection,
  ChartBlock,
  ChartTitle,
  ChartItem,
  ChartItemLabel,
  ChartItemBar,
  ChartItemBarFill,
  ChartItemValue,
  PageLayout,
} from "./reportDetail.styled2";
import { DEPARTMENTS } from "../../../../constants";
import { useTranslation } from "react-i18next";

const ReportDetailView2 = ({ data, skills, report }) => {
  const { t, i18n } = useTranslation();

  if (!data) return null;

  const {
    department,
    year,
    numEvaluations,
    averageGoals,
    averageCompetencies,
    averageFinal,
    goals,
    competencies,
  } = data;

  const goalsArray = goals
    ? Object.entries(goals).map(([key, value]) => ({
        label: key.replace("goals", "Objetivo "),
        value: parseFloat(value),
      }))
    : [];

  const competenciesArray = competencies
    ? Object.entries(competencies).map(([key, value]) => ({
        label: key.replace("competencies", "Competencia "),
        value: parseFloat(value),
      }))
    : [];

  const competenciesLabels = {};
  if (skills && Array.isArray(skills)) {
    skills.forEach((skill, index) => {
      const currentLang = i18n.language; // 'es' o 'pt'
      const translationKey = `translation.${currentLang}`;
      const label =
        skill[translationKey] || skill.translation?.es || skill.name;
      competenciesLabels[`Competencia ${index + 1}`] = label;
    });
  }

  const departmentName =
    t(DEPARTMENTS.find((dep) => dep.code === department)?.label) || department;

  return (
    <Container hasReport={!!report}>
      <Header>
        <HeaderLeft>
          <Title>
            {t("forms.report.detail.department")} {departmentName} {year}
          </Title>
          {/* <Year>Año {year}</Year> */}
        </HeaderLeft>
        <HeaderRight></HeaderRight>
      </Header>
      <PageLayout>
        <ScoreCardsContainer>
          <EvaluationsBadge>
            <div
              style={{
                fontSize: "2.5rem",
                fontWeight: "bold",
                marginBottom: "0.5rem",
              }}
            >
              {numEvaluations}
            </div>
            <div style={{ fontSize: "0.9rem" }}>
              {t("forms.report.detail.evaluationsLaunched")}
            </div>
          </EvaluationsBadge>
          <ScoreCard>
            <CircularChart>
              <svg width="140" height="140" viewBox="0 0 140 140">
                <circle
                  cx="70"
                  cy="70"
                  r="60"
                  fill="none"
                  stroke="#e8ecf1"
                  strokeWidth="12"
                />
                <circle
                  cx="70"
                  cy="70"
                  r="60"
                  fill="none"
                  stroke="#203b34"
                  strokeWidth="12"
                  strokeDasharray={`${(averageGoals / 5) * 377} 377`}
                  transform="rotate(-90 70 70)"
                  strokeLinecap="round"
                />
              </svg>
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "#666",
                    marginBottom: "0.25rem",
                  }}
                >
                  {t("forms.report.detail.goals")}
                </div>
                <div
                  style={{
                    fontSize: "1.8rem",
                    fontWeight: "bold",
                    color: "#203b34",
                  }}
                >
                  {averageGoals?.toFixed(2)}
                </div>
              </div>
            </CircularChart>
            {/* <ScoreCardTitle>Objetivos</ScoreCardTitle> */}
          </ScoreCard>

          <ScoreCard $primary>
            <CircularChart>
              <svg width="160" height="160" viewBox="0 0 160 160">
                <circle
                  cx="80"
                  cy="80"
                  r="70"
                  fill="none"
                  stroke="rgba(255, 255, 255, 0.2)"
                  strokeWidth="14"
                />
                <circle
                  cx="80"
                  cy="80"
                  r="70"
                  fill="none"
                  stroke="#f9ae17"
                  strokeWidth="14"
                  strokeDasharray={`${(averageFinal / 5) * 440} 440`}
                  transform="rotate(-90 80 80)"
                  strokeLinecap="round"
                />
              </svg>
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "0.9rem",
                    color: "#f9ae17",
                    marginBottom: "0.5rem",
                    fontWeight: "600",
                  }}
                >
                  {t("forms.report.detail.result")}
                </div>
                <div
                  style={{
                    fontSize: "2.5rem",
                    fontWeight: "bold",
                    color: "#ffffff",
                  }}
                >
                  {averageFinal?.toFixed(2)}
                </div>
                <div
                  style={{ fontSize: "0.8rem", color: "#ffffff", opacity: 0.8 }}
                >
                  {t("forms.report.detail.global")}
                </div>
              </div>
            </CircularChart>
          </ScoreCard>

          <ScoreCard>
            <CircularChart>
              <svg width="140" height="140" viewBox="0 0 140 140">
                <circle
                  cx="70"
                  cy="70"
                  r="60"
                  fill="none"
                  stroke="#e8ecf1"
                  strokeWidth="12"
                />
                <circle
                  cx="70"
                  cy="70"
                  r="60"
                  fill="none"
                  stroke="#f9ae17"
                  strokeWidth="12"
                  strokeDasharray={`${(averageCompetencies / 5) * 377} 377`}
                  transform="rotate(-90 70 70)"
                  strokeLinecap="round"
                />
              </svg>
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "0.7rem",
                    color: "#666",
                    marginBottom: "0.25rem",
                  }}
                >
                  {t("forms.report.detail.competencies")}
                </div>
                <div
                  style={{
                    fontSize: "1.8rem",
                    fontWeight: "bold",
                    color: "#f9ae17",
                  }}
                >
                  {averageCompetencies?.toFixed(2)}
                </div>
              </div>
            </CircularChart>
            {/* <ScoreCardTitle>Competencias</ScoreCardTitle> */}
          </ScoreCard>
        </ScoreCardsContainer>

        <MainContent>
          <ChartsSection>
            <ChartBlock>
              <ChartTitle>
                <i className="fas fa-bullseye"></i>{" "}
                {t("forms.report.detail.goalsTitle")}
              </ChartTitle>
              {goalsArray.map((goal, index) => (
                <ChartItem key={index}>
                  <ChartItemLabel>{goal.label}</ChartItemLabel>
                  <ChartItemBar>
                    <ChartItemBarFill
                      $width={(goal.value / 5) * 100}
                      $index={index}
                    />
                  </ChartItemBar>
                  <ChartItemValue>{goal.value?.toFixed(2)}</ChartItemValue>
                </ChartItem>
              ))}
            </ChartBlock>

            <ChartBlock>
              <ChartTitle>
                <i className="fas fa-award"></i>{" "}
                {t("forms.report.detail.competenciesTitle")}
              </ChartTitle>
              {competenciesArray.map((competency, index) => (
                <ChartItem key={index}>
                  <ChartItemLabel>
                    {competenciesLabels[competency.label] || competency.label}
                  </ChartItemLabel>
                  <ChartItemBar>
                    <ChartItemBarFill
                      $width={(competency.value / 5) * 100}
                      $index={index}
                      $competency
                    />
                  </ChartItemBar>
                  <ChartItemValue>
                    {competency.value?.toFixed(2)}
                  </ChartItemValue>
                </ChartItem>
              ))}
            </ChartBlock>
          </ChartsSection>
        </MainContent>
      </PageLayout>
    </Container>
  );
};

ReportDetailView2.propTypes = {
  data: PropTypes.shape({
    department: PropTypes.string,
    year: PropTypes.number,
    numEvaluations: PropTypes.number,
    averageGoals: PropTypes.number,
    averageCompetencies: PropTypes.number,
    averageFinal: PropTypes.number,
    goals: PropTypes.object,
    competencies: PropTypes.object,
  }),
};

export default ReportDetailView2;
